/**
 * Aesthetic drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const aestheticDrawingsPack: ToilePack = {
    slug: 'aesthetic-drawings',
    category: 'coloring',
    name: 'Aesthetic drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'moon', label: 'Moon and flowers', subject: 'a crescent moon with wildflowers growing out of its curve and a few small stars around it'},
        {key: 'window', label: 'Window view', subject: 'an open window with a sheer curtain, a vase of tulips on the sill and clouds outside'},
        {key: 'vase', label: 'Face vase', subject: 'a vase shaped as a calm face in profile with eyes closed, holding a stem of eucalyptus and a single rose'},
    ],
    styles: [
        {key: 'finepen', label: 'Fine pen', style: 'a hair-thin fine pen, light black lines, tiny dots and small stars scattered around', palette: 'Black ink only, very light touch.', framing: 'The drawing sits centered on a tall page with generous white space around it.'},
        {key: 'oneline', label: 'One line', style: 'a continuous one-line drawing, a single unbroken fine black line', palette: 'Black ink only.', framing: 'The drawing sits centered on a tall page with generous white space around it.'},
        {key: 'wash', label: 'Soft wash', style: 'fine black pen line under pale watercolor washes, dreamy and slightly blurred', palette: 'Pale sage and blush washes over the line, the ground white.', framing: 'The drawing sits centered on a tall page with generous white space around it.'},
    ],
    directions: ['with butterflies', 'mushrooms', 'with a cat'],
    directionHint: 'try: with butterflies',
    seo: {
        title: 'Aesthetic Drawings: Moon, Window, Face Vase',
        metaDescription: 'Nine soft aesthetic drawings, a moon with wildflowers, a window view and a face vase, in fine pen, one continuous line and soft wash.',
        h1: 'Aesthetic drawings: moon and flowers, window view, face vase',
        tagline: 'Delicate fine-pen drawings with lots of white space around them.',
        intro: 'Aesthetic drawings are soft, dreamy drawings that fill a sketchbook page or a phone wallpaper: moons, flowers, windows and small still lives. These nine pages show delicate fine-pen drawings with a lot of white space: a moon with wildflowers, a window view, a face-shaped vase. Each comes as hair-thin fine pen, a single unbroken line, or fine line under pale washes. For another subject, type it in the direction box.',
        useCases: [
            'A sketchbook page',
            'A phone wallpaper',
            'A journal cover',
            'A quiet drawing practice',
        ],
        faq: [
            {q: 'Does the drawing fill the whole page?', a: 'No. Each drawing sits centered on a tall page with generous white space around it.'},
            {q: 'What is the one-line style?', a: 'A continuous fine black line drawn without lifting the pen from the paper.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type it in the direction box, such as with butterflies, mushrooms or with a cat. A typed subject is a request, so the result may differ from the samples.'},
        ],
    },
}
